import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import type { MultiColumnData } from '../../types/chat';

export const DataChart = ({ data }: { data: MultiColumnData }) => {
  const chartData = data.data.map(row => {
    const chartItem: any = {};
    data.headers.forEach(header => {
      chartItem[header] = row[header];
    });
    return chartItem;
  });

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const dataPoint = payload[0].payload;
      return (
        <div className="bg-purple-50 rounded-lg border border-purple-200 shadow-lg p-3">
          {data.headers.map((header, index) => (
            <div key={header} className="flex justify-between items-center gap-4 mb-1 last:mb-0">
              <span className="font-semibold text-xs text-[#4c3794] capitalize">
                {header.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}:
              </span>
              <span className="text-xs text-gray-700 font-medium">
                {typeof dataPoint[header] === 'number' && dataPoint[header] % 1 !== 0
                  ? dataPoint[header].toFixed(2)
                  : dataPoint[header]}
              </span>
            </div>
          ))}
        </div>
      );
    }
    return null;
  };

  if (data.dimension === 2) {
    return (
      <div className="mt-4 rounded-xl p-4 border border-gray-200 bg-white">
        <h3 className="font-semibold text-[#4c3794] mb-4 text-base">
          {data.title}
        </h3>
        <ResponsiveContainer width="100%" height={250}>
          <BarChart data={chartData} margin={{ top: 20, right: 30, left: -10, bottom: 10 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#ede7f6" />
            <XAxis
              dataKey={data.headers[0]}
              angle={-45}
              textAnchor="end"
              height={70}
              tick={{ fontSize: 12, fill: '#4c3794' }}
              tickLine={{ stroke: '#b39ddb' }}
            />
            <YAxis
              tick={{ fontSize: 12, fill: '#4c3794' }}
              tickLine={{ stroke: '#b39ddb' }}
            />
            <Tooltip content={<CustomTooltip />} />
            <Bar
              dataKey={data.headers[1]}
              radius={[4, 4, 0, 0]}
              maxBarSize={50}
              fill="#4c3794"
            />
          </BarChart>
        </ResponsiveContainer>
      </div>
    );
  } else if (data.dimension === 3) {
    const categories = Array.from(new Set(chartData.map(item => item[data.headers[0]])));

    return (
      <div className="mt-4 rounded-xl p-4 border border-gray-200 bg-white">
        <h3 className="font-semibold text-[#4c3794] mb-4 text-base">
          {data.title}
        </h3>
        <ResponsiveContainer width="100%" height={250}>
          <LineChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#ede7f6" />
            <XAxis
              dataKey={data.headers[1]}
              angle={-45}
              textAnchor="end"
              height={70}
              tick={{ fontSize: 12, fill: '#4c3794' }}
              tickLine={{ stroke: '#b39ddb' }}
            />
            <YAxis
              tick={{ fontSize: 12, fill: '#4c3794' }}
              tickLine={{ stroke: '#b39ddb' }}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend />
            {categories.map((category, index) => {
              const categoryData = chartData.filter(item => item[data.headers[0]] === category);
              const hue = 270 + index * 30; // Purple hue range (270-330)
              return (
                <Line
                  key={category}
                  type="monotone"
                  dataKey={data.headers[2]}
                  data={categoryData}
                  name={category}
                  stroke={`hsl(${hue}, 70%, 50%)`}
                  strokeWidth={2}
                  dot={{
                    r: 4,
                    fill: `hsl(${hue}, 70%, 50%)`,
                    strokeWidth: 2,
                    stroke: '#fff'
                  }}
                  activeDot={{
                    r: 6,
                    fill: `hsl(${hue}, 70%, 50%)`,
                    stroke: '#fff',
                    strokeWidth: 2
                  }}
                />
              );
            })}
          </LineChart>
        </ResponsiveContainer>
      </div>
    );
  } else {
    return null;
  }
};
